import React, { useState } from "react";
import { ShieldCheck, Download, Trash2, Loader2, Scale, Globe } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CreditNote from "@/components/CreditNote";

const LAWS = [
  ["🇮🇳 Bharat (India)", "Digital Personal Data Protection Act, 2023 (DPDP)"],
  ["🇪🇺 EU / EEA", "General Data Protection Regulation (GDPR)"],
  ["🇬🇧 United Kingdom", "UK GDPR & Data Protection Act 2018"],
  ["🇺🇸 California, USA", "CCPA / CPRA"],
  ["🇧🇷 Brazil", "LGPD"],
  ["🇨🇦 Canada", "PIPEDA"],
  ["🇸🇬 Singapore", "PDPA"],
  ["🌏 Others", "APPI (JP), PIPL (CN), K-PIPA (KR) and applicable local laws"],
];

export default function Legal({ mode = "privacy" }) {
  const { user } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");

  const download = async () => {
    setBusy("dl");
    try {
      const res = await api.get("/me/export-data", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "anxya-my-data.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data is downloading 🌿");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const del = async () => {
    if (!window.confirm("Permanently delete your My Kind account and all personal data? This cannot be undone.")) return;
    setBusy("del");
    try {
      await api.post("/me/delete-account");
      localStorage.clear();
      toast.success("Account deleted. Take care 🙏");
      navigate("/"); setTimeout(() => window.location.reload(), 600);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(""); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-24 pb-20" data-testid={`legal-${mode}`}>
      <div className="flex items-center gap-2 text-brand mb-2"><Scale size={18} /><span className="text-[11px] uppercase tracking-widest">My Kind · Legal</span></div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{mode === "terms" ? "Terms of Use" : "Privacy Policy"}</h1>
      <p className="text-sec text-sm mt-2">Last updated: 20 August 2026 · One family, Vasudhaiva Kutumbakam 🌿</p>

      {mode === "privacy" ? (
        <div className="mt-8 space-y-6 text-sm leading-relaxed">
          <Section title="Our promise">My Kind is built on data sovereignty — you own your data. We collect only what's needed to run the features you use, never sell your personal data, and keep everything ad-free.</Section>
          <Section title="What we collect">Account details (name, email/phone), content you create (posts, bookings, health/regimen inputs, documents you choose to interpret), and basic usage/location (country) to price fairly and localise. Sensitive inputs (e.g. health, blood group) are used only to generate your requested result.</Section>
          <Section title="How we use it">To provide the service, personalise your experience, process region-fair credits, keep the community kind and safe, and comply with law. Consent is our primary lawful basis; you can withdraw it anytime.</Section>
          {mode !== "terms" && <CreditNote />}
          <Section title="Your rights (region-aware)">Depending on where you live, you have rights to access, correct, export (portability), delete (erasure), restrict/object to processing, and withdraw consent. Use the tools below to exercise them instantly.</Section>
          <Section title="Laws we align to">
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              {LAWS.map(([r, l]) => (<div key={r} className="rud-card rounded-lg p-2.5"><div className="text-brand text-xs">{r}</div><div className="text-xs text-sec">{l}</div></div>))}
            </div>
          </Section>
          <Section title="Data Protection / Grievance Officer (DPDP §13)">
            For any privacy request or grievance, contact our Grievance Officer at <a href="mailto:privacy@anxya.tech" className="text-brand">privacy@anxya.tech</a>. We aim to respond within the timelines your local law requires (e.g. 30 days under GDPR; promptly under DPDP).
          </Section>
          <Section title="Retention, security & transfers">We keep data only as long as needed for the purpose or as law requires, protect it with encryption in transit and access controls, and store files in secure object storage (never sold). Cross-border transfers use appropriate safeguards.</Section>
          <Section title="Children">My Kind is not intended for children below the age of digital consent in your country without verifiable parental consent (as DPDP/GDPR-K require).</Section>

          {user && (
            <div className="rud-card rounded-2xl p-5 mt-4" data-testid="data-rights">
              <div className="flex items-center gap-2 mb-3"><ShieldCheck className="text-brand" size={18} /><h3 className="font-serif text-xl">Exercise your rights now</h3></div>
              <div className="flex flex-wrap gap-3">
                <button data-testid="export-data-btn" onClick={download} disabled={busy === "dl"} className="btn-ghost rounded-full h-10 px-4 text-sm flex items-center gap-2 disabled:opacity-60">{busy === "dl" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download my data</button>
                <button data-testid="delete-account-btn" onClick={del} disabled={busy === "del"} className="rounded-full h-10 px-4 text-sm flex items-center gap-2 border border-red-500/40 text-red-400 disabled:opacity-60">{busy === "del" ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete my account</button>
              </div>
              <p className="text-[11px] text-sec mt-2">Export gives you a JSON of everything we hold. Deletion is permanent and immediate.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 space-y-6 text-sm leading-relaxed">
          <Section title="Acceptance">By using My Kind you agree to these Terms and our Privacy Policy. If you don't agree, please don't use the service.</Section>
          <Section title="Kind community">My Kind is a hate-free, kind community. Content that is hateful, divisive, harassing or unlawful is not allowed and may be moderated or removed.</Section>
          <Section title="Credits & payments">Some features use region-fair credits. Community Events/Meals are settled in real currency (host keeps 85%, platform 15%). Purchases and fees are shown before you confirm.</Section>
          <Section title="Your content & IP">You own your content and IP. You grant My Kind a limited licence to host and display it so the service works. You're responsible for what you post.</Section>
          <Section title="No professional advice">AI outputs (health regimen, interpretations, compliance notes, mock tests) are for information only — not medical, legal, financial or professional advice.</Section>
          <Section title="Liability & changes">The service is provided "as is". We may update these Terms; continued use means acceptance. Governing law follows your region where required.</Section>
          <Section title="Contact">Questions? <a href="mailto:privacy@anxya.tech" className="text-brand">privacy@anxya.tech</a>.</Section>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (<div><h2 className="font-serif text-xl text-brand">{title}</h2><div className="text-sec mt-1">{children}</div></div>);
}
